import { GTP_BET_BUILDER_COMBINATION_TYPE } from '~Betslip/uiModes/shared/GTPBetBuilder/constants';
import { BET_TRACKING_TYPE } from '~spa/Components/BetHistory/BetTracking/constants.js';
import { generateEventMarketKey } from '~spa/Components/BetHistory/BetTracking/utils.js';

const KEY_STRATEGIES = {
    [GTP_BET_BUILDER_COMBINATION_TYPE]: (ids) => ids.toSorted().join('_'),
    [BET_TRACKING_TYPE]: (ids) => generateEventMarketKey(ids),
};

/**
 * Sportsbook_live_data subscription tracking.
 */
class sbLiveDataSubscriptionStoreClass {
    /**
     * @var {map} #subscriptions { entityIdentifier -> <object>subscription_data }
     * An entity could be a gtp_betbuilder combination selection (e.g. bb_123_456). The
     * subscription data should be the entire payload needed to send to SDL, except for the "action" property.
     * <Map>( type => <Map>subscriptions )
     */
    #subscriptionsByType = new Map();

    /**
     * @var {map} #subscriptionCounts { entityIdentifier -> <int>count }
     */
    #subscriptionCountsByType = new Map();

    /**
     * Generates a unique cache key based on subscription type and IDs.
     * @param {string} type The subscription type identifier (same as used in the wsMessage)
     * @param {Array|Object} idGroup
     *     - Array of selection IDs (for gtp_betbuilder)
     *     - Object containing event/market IDs (for bet_tracking)
     * @returns {string} A composite key. e.g. `gtp_betbuilder_100_123_999` or `bet_tracking_1_2`
     */
    #getKey(type, idGroup) {
        const strategy = KEY_STRATEGIES[type];

        if (!strategy) {
            throw new Error(`wsMessage type "${type}" is not recognised...`);
        }

        const values = strategy(idGroup);

        return `${type}_${values}`;
    }

    /**
     * Retrieve subscriptions for a specified type
     * @param {string} type e.g. gtp_betbuilder, bet_tracking
     * @returns {map} subset of this.#subscriptionsByType
     */
    getSubscriptionsByType(type) {
        const subscriptions = this.#subscriptionsByType.get(type);
        if (subscriptions) {

            return subscriptions;
        }

        const emptySubscriptions = new Map();
        this.#subscriptionsByType.set(type, emptySubscriptions);

        return emptySubscriptions;
    }

    /**
     * Retrieve subscription counts for a specified type
     * @param {string} type e.g. gtp_betbuilder, bet_tracking
     * @returns {map} subset of this.#subscriptionCountsByType
     */
    #getSubscriptionCountsByType(type) {
        const counts = this.#subscriptionCountsByType.get(type);
        if (counts) {

            return counts;
        }

        const emptyCounts = new Map();
        this.#subscriptionCountsByType.set(type, emptyCounts);

        return emptyCounts;
    }

    /**
     * Clear subscription data relating to a specified type
     * @param {string} type e.g. gtp_betbuilder, bet_tracking
     * @returns {void}
     */
    #clearSubscriptionsByType(type) {
        this.#subscriptionsByType.set(type, new Map());
        this.#subscriptionCountsByType.set(type, new Map());
    }

    /**
     * Increment the reference count for a subscription idGroup.
     * If the subscription does not yet exist, it is added with a count of 1.
     *
     * @param {string} type subscription type identifier (e.g., 'gtp_betbuilder', 'bet_tracking')
     * @param {Array|Object} idGroup array of IDs or object containing IDs
     * @returns {number} the updated reference count for this idGroup
     */
    #incrementSubscription(type, idGroup) {
        const subscriptionKey = this.#getKey(type, idGroup);
        const subscriptionsMap = this.getSubscriptionsByType(type);
        const subscriptionCountsMap = this.#getSubscriptionCountsByType(type);

        const existingSubscription = subscriptionsMap.get(subscriptionKey);
        const newCount = existingSubscription ? subscriptionCountsMap.get(subscriptionKey) + 1 : 1;
        if (!existingSubscription) {
            subscriptionsMap.set(subscriptionKey, idGroup);
        }

        subscriptionCountsMap.set(subscriptionKey, newCount);

        return newCount;
    }

    /**
     * Decrement the reference count for a subscription idGroup.
     * If the count reaches 0, the idGroup is fully removed.
     * If the idGroup does not exist, a warning is logged and 0 is returned.
     *
     * @param {string} type subscription type identifier (e.g., 'gtp_betbuilder', 'bet_tracking')
     * @param {Array|Object} idGroup array of IDs or object containing IDs
     * @returns {number} the updated reference count for this idGroup (0 if removed or not found)
     */
    #decrementSubscription(type, idGroup) {
        const subscriptionKey = this.#getKey(type, idGroup);
        const subscriptionsMap = this.getSubscriptionsByType(type);
        const subscriptionCountsMap = this.#getSubscriptionCountsByType(type);

        const existingSubscription = subscriptionsMap.get(subscriptionKey);
        if (!existingSubscription) {
            // eslint-disable-next-line no-console
            console.warn(`Warning: sbLiveDataSubscriptionStoreClass::decrementSubscription() - key "${subscriptionKey}" does not exist`);

            return 0;
        }

        const newCount = subscriptionCountsMap.get(subscriptionKey) - 1;
        if (newCount <= 0) {
            subscriptionsMap.delete(subscriptionKey);
            subscriptionCountsMap.delete(subscriptionKey);

            return 0;
        }

        subscriptionCountsMap.set(subscriptionKey, newCount);

        return newCount;
    }

    /**
     * Process a subscribe request by incrementing its reference count for a specified type
     * @param {object} payload the wsMessage payload
     * @param {string} payload.type e.g. gtp_betbuilder, bet_tracking
     * @param {array} payload.idGroups
     *   - array of:
     *     - Arrays of selection IDs (for gtp_betbuilder)
     *     - Objects containing event/market IDs (for bet_tracking)
     * @returns {Array|false} Array of new subscription ID groups to send via websocket,
     *   or `false` if all subscriptions already exist (no websocket message needed)
     */
    subscribe(payload) {
        const { type, idGroups } = payload;

        const filteredPayload = [];
        idGroups.forEach(idGroup => {
            const newSubscriptionCount = this.#incrementSubscription(type, idGroup);
            if (newSubscriptionCount) {
                filteredPayload.push(idGroup);
            }
        });

        if (!filteredPayload.length) {

            return false;
        }

        return filteredPayload;
    }

    /**
     * Process an usubscribe request by decrementing subscription counts for a specified type or by
     * clearing all subscription counts if the payload.idGroups is null
     * @param {object} payload the wsMessage payload
     * @param {string} payload.type e.g. gtp_betbuilder, bet_tracking
     * @param {array|null} payload.idGroups
     *   - array of:
     *     - Arrays of selection IDs (for gtp_betbuilder)
     *     - Objects containing event/market IDs (for bet_tracking)
     *   - null → unsubscribe from all for the given type
     * @returns {Array|null|false} Array of ID groups to unsubscribe from via websocket,
     *   `null` if unsubscribing from all subscriptions of this type,
     *   or `false` if no subscriptions need to be removed (no websocket message needed)
     */
    unsubscribe(payload) {
        const { type, idGroups } = payload;
        if (idGroups === null) {
            // pass null to unregister from all in SLD
            this.#clearSubscriptionsByType(type);

            return idGroups;
        }

        const filteredPayload = [];
        idGroups.forEach(idGroup => {
            const newSubscriptionCount = this.#decrementSubscription(type, idGroup);

            if (!newSubscriptionCount) {
                filteredPayload.push(idGroup);
            }
        });

        if (!filteredPayload.length) {

            return false;
        }

        return filteredPayload;
    }

    /**
     * Get a list of current subscriptions for a subscription type.
     * This list is generally used in an onConnection callback to resubscribe in the event of a
     * dropped and reestablished websocket connection
     * Note the use of an arrow function to preserve the 'this' binding
     * @returns {array} an array of current subscription data
     */
    getSubscriptions = () => {
        const subscriptionPayloads = {};
        this.#subscriptionsByType.keys().forEach(
            key => {
                subscriptionPayloads[key] = Array.from(this.#subscriptionsByType.get(key).values());
            }
        );

        return subscriptionPayloads;
    };

    /**
     * Clear all subscription data and subscription counts, e.g. when a websocket connection is closed
     * This list is generally used in an onKill callback
     * Note the use of an arrow function to preserve the 'this' binding
     * @returns {void}
     */
    clearSubscriptions = () => {
        this.#subscriptionsByType = new Map();
        this.#subscriptionCountsByType = new Map();
    };
}

const sbLiveDataSubscriptionStore = new sbLiveDataSubscriptionStoreClass();

export default sbLiveDataSubscriptionStore;
